import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Lazy-load DevTool only in development to avoid bundling devDependency in production
const DevTool =
  process.env.NODE_ENV === 'development'
    ? lazy(() =>
        import('@hookform/devtools').then((module) => ({
          default: module.DevTool,
        })),
      )
    : () => null;

import { FormProvider, useForm } from 'react-hook-form';

/**
 * @typedef {import('../../../types/provider').Provider} Provider
 * @typedef {import('../../../types/location').LocationOption} LocationOption
 * @typedef {import('../types').RosterProvider} RosterProvider
 */

import {
  MAX_PROVIDERS_LIMIT,
  MAX_PROVIDERS_PER_SESSION,
} from '../../../constants/subscriptions';
import { getProviderBlockerMessage } from '../validation';
import { FIELDS } from './constants';
import EmailField from './fields/EmailField';
import Last4Field from './fields/Last4Field';
import LocationField from './fields/LocationField';
import NameField from './fields/NameField';
import NpiField from './fields/NpiField';
import ProviderSearchResult from './ProviderSearchResult';

const DEFAULT_VALUES = Object.freeze({
  [FIELDS.npi]: '',
  [FIELDS.locationCity]: '',
  [FIELDS.name]: '',
  [FIELDS.last4]: '',
  [FIELDS.verifiedLast4]: '',
  [FIELDS.email]: '',
});

/**
 * Error banner for displaying validation/limit messages.
 * @param {{ children: React.ReactNode }} props
 */
const ErrorBanner = ({ children }) => (
  <div
    className="w-full px-4 py-3 rounded bg-coral-05 text-coral-96 text-sm font-semibold"
    role="alert"
    aria-live="polite"
    aria-atomic="true"
  >
    {children}
  </div>
);

/**
 * Allows users to search for a provider, validate their identity, and add to roster.
 *
 * Flow:
 * 1. User searches by NPI or Location+Name
 * 2. If provider is "good" (eligible + unique), Last4 and Email fields enable
 * 3. User verifies identity with Last4 and provides email
 * 4. User clicks Add to add provider to roster
 *
 * @param {{
 *   onAdd: (provider: RosterProvider) => void,
 *   disabled?: boolean,
 *   existingProviders?: Array<RosterProvider> - Providers in current session
 *   existingRosterProviders?: Array<{providerCode?: string}> - Providers already in user's roster (from backend)
 * }} props
 */
const ProviderSearch = ({
  onAdd,
  disabled = false,
  existingProviders = [],
  existingRosterProviders = [],
}) => {
  // ─────────────────────────────────────────────────────────────────────────
  // Form state via React Hook Form
  // ─────────────────────────────────────────────────────────────────────────
  const methods = useForm({
    mode: 'onBlur',
    defaultValues: DEFAULT_VALUES,
  });

  const {
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = methods;

  // Watch form values (RHF is the source of truth)
  const {
    [FIELDS.locationCity]: locationCityValue,
    [FIELDS.verifiedLast4]: verifiedLast4Value,
    [FIELDS.email]: emailValue,
  } = watch() || '';

  // ─────────────────────────────────────────────────────────────────────────
  // Provider search state
  // ─────────────────────────────────────────────────────────────────────────
  const [foundProvider, setFoundProvider] = useState(
    /** @type {Provider|null} */ (null),
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Derived state using pure validation functions
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Merges session providers (current checkout session) and existing roster providers
   * (from backend) to create a comprehensive list for duplicate checking.
   *
   * This ensures we prevent adding:
   * 1. Providers already in the current session (existingProviders)
   * 2. Providers already in the user's roster (existingRosterProviders)
   *
   * @type {Array<{providerCode: string}>}
   */
  const allExistingProviders = useMemo(() => {
    const sessionProviderCodes = new Set(
      existingProviders.map((p) => p.providerCode).filter(Boolean),
    );
    const rosterProviderCodes = new Set(
      existingRosterProviders.map((p) => p.providerCode).filter(Boolean),
    );

    // Combine both sets to get all unique provider codes
    const allCodes = new Set([...sessionProviderCodes, ...rosterProviderCodes]);

    // Return array with providerCode for validation
    return Array.from(allCodes).map((code) => ({ providerCode: code }));
  }, [existingProviders, existingRosterProviders]);

  const blockerMessage = getProviderBlockerMessage(
    foundProvider,
    allExistingProviders,
  );

  // Block adding more than 25 providers per checkout session
  const hasReachedMaxProviders =
    existingProviders.length >= MAX_PROVIDERS_PER_SESSION;

  const providerGood =
    !!foundProvider && !blockerMessage && !hasReachedMaxProviders;

  // verifiedLast4 is set by Last4Field only when API confirms validity
  const validLast4 = Boolean(verifiedLast4Value);
  const validEmail = Boolean(emailValue) && !errors[FIELDS.email];

  // Can add when: provider is good + last4 verified + email valid
  const canAdd = providerGood && isValid;

  const fieldsDisabled = disabled || hasReachedMaxProviders;

  // Build RosterProvider when all criteria met
  const rosterProvider = useMemo(() => {
    if (!foundProvider || !canAdd) return null;

    return /** @type {RosterProvider} */ ({
      ...foundProvider,
      validEmail,
      validLast4,
      email: emailValue,
      last4: verifiedLast4Value,
    });
  }, [
    foundProvider,
    canAdd,
    emailValue,
    verifiedLast4Value,
    validEmail,
    validLast4,
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // Field reset helpers
  // ─────────────────────────────────────────────────────────────────────────
  const resetFieldToDefault = useCallback(
    /** @param {string} fieldName */
    (fieldName) => {
      setValue(fieldName, DEFAULT_VALUES[fieldName], {
        shouldValidate: true,
        shouldDirty: false,
      });
    },
    [setValue],
  );

  const resetVerificationFields = useCallback(() => {
    resetFieldToDefault(FIELDS.last4);
    resetFieldToDefault(FIELDS.verifiedLast4);
    resetFieldToDefault(FIELDS.email);
  }, [resetFieldToDefault]);

  const resetAllFields = useCallback(() => {
    Object.keys(DEFAULT_VALUES).forEach(resetFieldToDefault);
    setFoundProvider(null);
    resetVerificationFields();
  }, [resetFieldToDefault, resetVerificationFields]);

  // ─────────────────────────────────────────────────────────────────────────
  // Field handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleNpiUpdatedProvider = useCallback(
    /**
     * When provider changes (from NPI or Name search), reset verification fields
     * @param {Provider|null} provider
     */
    (provider) => {
      setFoundProvider(provider);
      resetVerificationFields();

      if (provider) {
        resetFieldToDefault(FIELDS.locationCity);
        resetFieldToDefault(FIELDS.name);
      }
    },
    [resetVerificationFields, resetFieldToDefault],
  );

  const handleNameUpdatedProvider = useCallback(
    /**
     * When provider changes (from NPI or Name search), reset verification fields
     * @param {Provider|null} provider
     */
    (provider) => {
      setFoundProvider(provider);
      resetVerificationFields();

      if (provider) {
        resetFieldToDefault(FIELDS.npi);
      }
    },
    [resetVerificationFields, resetFieldToDefault],
  );

  const handleLocationChange = useCallback(
    /**
     * When location changes, reset name field and any found provider
     * @param {LocationOption|null} _location
     */
    (_location) => {
      console.log('handleLocationChange', { _location });
      resetFieldToDefault(FIELDS.name);
      resetFieldToDefault(FIELDS.npi);
      setFoundProvider(null);
      resetVerificationFields();
    },
    [resetFieldToDefault, resetVerificationFields],
  );

  /**
   * Add provider to roster after validating all fields
   */
  const handleAdd = useCallback(() => {
    const onSubmitSuccess = () => {
      if (!rosterProvider) return;

      onAdd(rosterProvider);
      resetAllFields();
    };

    handleSubmit(onSubmitSuccess)();
  }, [handleSubmit, onAdd, rosterProvider, resetAllFields]);

  // ─────────────────────────────────────────────────────────────────────────
  // Disable verification fields until we have a "good" provider
  // ─────────────────────────────────────────────────────────────────────────
  const disableVerificationFields = !providerGood || fieldsDisabled;

  // Get provider code for Last4 validation
  const providerCode = foundProvider?.providerCode?.trim() || '';

  // Only count errors that have actual error messages
  const hasAnyErrorMessages = Object.values(errors || {}).some(
    (error) => error?.message && String(error.message).trim().length > 0,
  );

  return (
    <div className="flex flex-col gap-6 w-full border-y border-gray-20 py-6">
      <FormProvider {...methods}>
        <div className="w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_1fr_1fr_1fr] gap-2 lg:gap-x-4 lg:gap-y-0">
            {/* NPI Field */}
            <NpiField
              onProviderUpdate={handleNpiUpdatedProvider}
              disabled={fieldsDisabled}
            />

            {/* OR Separator */}
            <div className="flex flex-col">
              <div className="lg:h-14 lg:pb-2" />
              <div className="lg:h-12 flex items-center justify-center px-3 gap-2">
                <div className="w-[72px] h-0.5 bg-blue-30 lg:hidden" />
                <span className="text-base font-semibold text-gray-600 whitespace-nowrap">
                  OR
                </span>
                <div className="w-[72px] h-0.5 bg-blue-30 lg:hidden" />
              </div>
              {hasAnyErrorMessages && (
                <div className="hidden lg:block pt-1 min-h-6" />
              )}
            </div>

            {/* Location Field */}
            <LocationField
              onLocationSelected={handleLocationChange}
              disabled={fieldsDisabled}
            />

            {/* Name Field */}
            <NameField
              locationCity={locationCityValue}
              onProviderUpdate={handleNameUpdatedProvider}
              disabled={fieldsDisabled}
            />

            {/* Last 4 Field */}
            <Last4Field
              disabled={disableVerificationFields}
              pwid={providerCode}
            />

            {/* Email Field */}
            <EmailField
              disabled={disableVerificationFields}
              existingProviders={existingProviders}
            />
          </div>
        </div>
      </FormProvider>

      {process.env.NODE_ENV === 'development' && (
        <Suspense fallback={null}>
          <DevTool control={control} />
        </Suspense>
      )}

      {/* Max providers warning */}
      {/* TODO: Update this to maximum checkout limit along with pull#165 checkout session*/}
      {hasReachedMaxProviders && (
        <ErrorBanner>
          {`You have reached the maximum number of elevated providers for a practice subscription. If you would like to add more than ${MAX_PROVIDERS_PER_SESSION} providers to your subscription, please reach out to us.`}
        </ErrorBanner>
      )}

      {/* Provider card with validation issues and Add button */}
      {!hasReachedMaxProviders && foundProvider && (
        <ProviderSearchResult
          provider={foundProvider}
          blockerMessage={blockerMessage}
          canAdd={canAdd}
          onAdd={handleAdd}
          disabled={fieldsDisabled}
        />
      )}
    </div>
  );
};

ProviderSearch.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  existingProviders: PropTypes.arrayOf(PropTypes.object),
  existingRosterProviders: PropTypes.arrayOf(PropTypes.object),
};

export default ProviderSearch;
